import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { useFieldControl } from "./Field";
import type { VbControlSize } from "./Input";

const TRIGGER_SIZE_CLASS: Record<VbControlSize, string> = {
  sm: "vb-select__trigger--sm",
  md: "", // base trigger is md (38px), matching Input
  lg: "vb-select__trigger--lg",
};

export type SelectOption = {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
};

/** One option row. Use directly when you need more than `options` gives you
 *  (groups, separators, custom content); otherwise pass `options`. */
export const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item ref={ref} className={cn("vb-select__item", className)} {...props}>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    <SelectPrimitive.ItemIndicator className="vb-select__check">
      <Check size={14} strokeWidth={3} />
    </SelectPrimitive.ItemIndicator>
  </SelectPrimitive.Item>
));
SelectItem.displayName = "SelectItem";

type Props = {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** Convenience list. Ignored when `children` is given. */
  options?: SelectOption[];
  /** SelectItem elements, for cases `options` can't express. */
  children?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  /** Set to submit a value with a surrounding native form. */
  name?: string;
  size?: VbControlSize;
  /** Force the error look. Inside a Field/FormRow with `error` set, this is
   *  already on — pass it only for a standalone control. */
  error?: boolean;
  id?: string;
  /** Applied to the trigger — that is the element laid out in a form. */
  className?: string;
  contentClassName?: string;
  "aria-label"?: string;
  /** Inherited from a surrounding Field; set it for a standalone select. */
  "aria-describedby"?: string;
};

/** Design-system select: the installed Radix Select restyled onto the v2
 *  tokens, the same way Modal/Drawer wrap Radix Dialog. Radix supplies typeahead,
 *  keyboard navigation and collision handling; the trigger is visually identical
 *  to Input so the two line up when stacked in a form. */
export const Select = React.forwardRef<HTMLButtonElement, Props>(function Select(
  {
    value,
    defaultValue,
    onValueChange,
    options,
    children,
    placeholder = "Select…",
    disabled,
    required,
    name,
    size = "md",
    error,
    id,
    className,
    contentClassName,
    "aria-label": ariaLabel,
    "aria-describedby": describedBy,
  },
  ref,
) {
  const f = useFieldControl({ id, describedBy, invalid: error });

  return (
    <SelectPrimitive.Root
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      disabled={disabled}
      required={required}
      name={name}
    >
      <SelectPrimitive.Trigger
        ref={ref}
        id={f.id}
        aria-label={ariaLabel}
        aria-describedby={f.describedBy}
        aria-invalid={f.invalid || undefined}
        className={cn(
          "vb-select__trigger",
          TRIGGER_SIZE_CLASS[size],
          f.invalid && "vb-select__trigger--error",
          className,
        )}
      >
        <SelectPrimitive.Value className="vb-select__value" placeholder={placeholder} />
        <SelectPrimitive.Icon className="vb-select__icon">
          <ChevronDown size={15} />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        {/* position="popper" is what publishes --radix-select-trigger-width,
            which the menu matches so it never renders narrower than its field. */}
        {/* Deliberately NOT `vb-root`, unlike Modal/Drawer's portalled panels.
            `.vb-root` is declared in vb.css, i.e. after vb-components.css, and
            sets `background: var(--vb-bg)` — at equal specificity it would beat
            this panel's own white and paint it canvas-grey. (Modal/Drawer opt in
            and are held white by the `.vb-root.vb-*-panel` compound added in
            AITS-494; this menu simply never opts in.) Nothing is lost:
            globals.css binds Figtree and --vb-text on :root app-wide since
            AITS-485, so the portal inherits the base look regardless. */}
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          className={cn("vb-select__content", contentClassName)}
        >
          <SelectPrimitive.ScrollUpButton className="vb-select__scroll">
            <ChevronUp size={14} />
          </SelectPrimitive.ScrollUpButton>

          <SelectPrimitive.Viewport className="vb-select__viewport">
            {children ??
              options?.map((o) => (
                <SelectItem key={o.value} value={o.value} disabled={o.disabled}>
                  {o.label}
                </SelectItem>
              ))}
          </SelectPrimitive.Viewport>

          <SelectPrimitive.ScrollDownButton className="vb-select__scroll">
            <ChevronDown size={14} />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
});
Select.displayName = "Select";

export default Select;
